import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaPaintBrush, FaEraser, FaFill, FaUndo, FaTrash, FaUser, FaChevronDown, FaChevronUp, FaImages, FaSave, FaCheck, FaTimes } from 'react-icons/fa';
import { useRealtimeCanvas } from '../hooks/useRealtimeCanvas';
import { useIsMobile } from '../hooks/useIsMobile';
import { useIsPWA } from '../hooks/useIsPWA';
import { DrawingGallery } from '../components/CanvasDrawingGallery';
import { collection, query, orderBy, onSnapshot, addDoc, serverTimestamp } from 'firebase/firestore';
import { db, storage } from '../firebaseConfig';
import { ref, uploadString } from 'firebase/storage';
import { SavedDrawing } from '../types/canvas';

interface NewCanvasSectionProps {
  userId: string;
  userName: string;
}

export const NewCanvasSection = ({ userId, userName }: NewCanvasSectionProps) => {
  const isMobile = useIsMobile();
  const isPWA = useIsPWA();
  const canvasId = 'main'; // Global canvas ID

  const [isLandscape, setIsLandscape] = useState(false);
  const [showToolbar, setShowToolbar] = useState(false);
  const [showGallery, setShowGallery] = useState(false);
  const [savedDrawings, setSavedDrawings] = useState<SavedDrawing[]>([]);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [drawingName, setDrawingName] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Canvas dimensions - landscape orientation, smaller for better flood fill performance
  const canvasWidth = 800;
  const canvasHeight = 450;

  const {
    canvasRef,
    currentTool,
    setCurrentTool,
    currentColor,
    setCurrentColor,
    lineWidth,
    setLineWidth,
    isDrawing,
    activeUsers,
    actions,
    handleStart,
    handleMove,
    handleEnd,
    clearCanvas,
    undoLastAction,
  } = useRealtimeCanvas({
    canvasId,
    userId,
    userName,
    width: canvasWidth,
    height: canvasHeight,
  });

  // Listen for saved drawings
  useEffect(() => {
    const q = query(
      collection(db, 'savedDrawings'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const drawings: SavedDrawing[] = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as SavedDrawing[];
      setSavedDrawings(drawings);
    });

    return () => unsubscribe();
  }, []);

  // Detect orientation changes
  useEffect(() => {
    const checkOrientation = () => {
      const landscape = window.innerWidth > window.innerHeight;
      setIsLandscape(landscape);

      // Auto-hide toolbar in landscape mode
      if (landscape) {
        setShowToolbar(false);
      }
    };

    checkOrientation();
    window.addEventListener('resize', checkOrientation);
    window.addEventListener('orientationchange', checkOrientation);

    return () => {
      window.removeEventListener('resize', checkOrientation);
      window.removeEventListener('orientationchange', checkOrientation);
    };
  }, []);

  // Save drawing function
  const handleSaveDrawing = async () => {
    if (!drawingName.trim() || !canvasRef.current) return;

    setIsSaving(true);
    try {
      const canvas = canvasRef.current;

      // Convert canvas to data URL
      const imageDataUrl = canvas.toDataURL('image/png');

      // Upload to Firebase Storage
      const timestamp = Date.now();
      const imageRef = ref(storage, `drawings/${userId}_${timestamp}.png`);
      await uploadString(imageRef, imageDataUrl, 'data_url');

      // Get download URL
      const imageUrl = imageDataUrl; // For now, store data URL directly

      // Save to Firestore
      await addDoc(collection(db, 'savedDrawings'), {
        name: drawingName.trim(),
        imageUrl,
        createdBy: userName,
        createdAt: serverTimestamp(),
        metadata: {
          width: canvasWidth,
          height: canvasHeight,
          strokeCount: actions.length,
        },
      });

      setDrawingName('');
      setShowSaveDialog(false);
      console.log('✅ Drawing saved successfully!');
    } catch (error) {
      console.error('❌ Error saving drawing:', error);
      alert('Error guardando el dibujo. Intenta de nuevo.');
    } finally {
      setIsSaving(false);
    }
  };

  const colors = [
    '#000000', '#FFFFFF', '#FF0000', '#00FF00',
    '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF',
    '#FFA500', '#800080', '#FFC0CB', '#A52A2A',
  ];

  const brushSizes = [1, 3, 5, 10, 15, 20];

  if (showGallery) {
    return <DrawingGallery drawings={savedDrawings} onBack={() => setShowGallery(false)} />;
  }

  return (
    <section
      className={`relative flex flex-col items-center justify-center ${
        isLandscape ? 'fixed inset-0 z-50 bg-black' : 'min-h-screen py-20'
      } p-0`}
      style={{
        backgroundColor: isLandscape ? '#000' : 'transparent',
        paddingTop: isLandscape ? 'env(safe-area-inset-top)' : undefined,
        paddingLeft: isLandscape ? 'env(safe-area-inset-left)' : undefined,
        paddingRight: isLandscape ? 'env(safe-area-inset-right)' : undefined,
        paddingBottom: isLandscape ? 'env(safe-area-inset-bottom)' : undefined,
      }}
    >
      <div className={`w-full h-full ${isLandscape ? 'flex flex-col' : 'max-w-7xl mx-auto space-y-6'}`}>

        {/* Landscape Top Collapsible Tab */}
        {isLandscape && (
          <div className="relative">
            {/* Collapsible Tab Button */}
            <button
              onClick={() => setShowToolbar(!showToolbar)}
              className="absolute top-0 left-1/2 transform -translate-x-1/2 z-[100] bg-white/90 backdrop-blur-md px-6 py-2 rounded-b-xl shadow-lg hover:bg-white transition-all flex items-center gap-2"
            >
              <span className="text-sm font-medium text-gray-700">Herramientas</span>
              {showToolbar ? <FaChevronUp className="text-gray-600" /> : <FaChevronDown className="text-gray-600" />}
            </button>

            {/* Sliding Toolbar from Top */}
            <AnimatePresence>
              {showToolbar && (
                <motion.div
                  initial={{ y: -400, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: -400, opacity: 0 }}
                  transition={{ type: 'spring', damping: 25 }}
                  className="absolute top-0 left-0 right-0 z-[99] bg-white/95 backdrop-blur-md border-b border-white/30 shadow-2xl"
                >
                  <div className="p-4 grid grid-cols-4 gap-4">
                    {/* Tools */}
                    <div className="space-y-2">
                      <h3 className="font-bold text-gray-700 text-xs">Herramientas</h3>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setCurrentTool('draw')}
                          className={`flex-1 p-2 rounded-lg transition-all ${
                            currentTool === 'draw'
                              ? 'bg-blue-500 text-white shadow-md'
                              : 'bg-white/50 hover:bg-white/80 text-gray-700'
                          }`}
                        >
                          <FaPaintBrush className="mx-auto" size={16} />
                        </button>
                        <button
                          onClick={() => setCurrentTool('erase')}
                          className={`flex-1 p-2 rounded-lg transition-all ${
                            currentTool === 'erase'
                              ? 'bg-blue-500 text-white shadow-md'
                              : 'bg-white/50 hover:bg-white/80 text-gray-700'
                          }`}
                        >
                          <FaEraser className="mx-auto" size={16} />
                        </button>
                        <button
                          onClick={() => setCurrentTool('fill')}
                          className={`flex-1 p-2 rounded-lg transition-all ${
                            currentTool === 'fill'
                              ? 'bg-blue-500 text-white shadow-md'
                              : 'bg-white/50 hover:bg-white/80 text-gray-700'
                          }`}
                        >
                          <FaFill className="mx-auto" size={16} />
                        </button>
                      </div>
                    </div>

                    {/* Colors */}
                    <div className="space-y-2 col-span-2">
                      <h3 className="font-bold text-gray-700 text-xs">Colores</h3>
                      <div className="flex gap-1.5 flex-wrap">
                        {colors.map((color) => (
                          <button
                            key={color}
                            onClick={() => setCurrentColor(color)}
                            className={`w-8 h-8 rounded-lg transition-all ${
                              currentColor === color
                                ? 'ring-4 ring-blue-500 scale-110'
                                : 'hover:scale-105'
                            }`}
                            style={{
                              backgroundColor: color,
                              border: color === '#FFFFFF' ? '2px solid #666' : 'none',
                            }}
                          />
                        ))}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="space-y-2">
                      <h3 className="font-bold text-gray-700 text-xs">Acciones</h3>
                      <div className="grid grid-cols-2 gap-1">
                        <button
                          onClick={undoLastAction}
                          className="p-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg transition-all text-xs"
                          title="Deshacer"
                        >
                          <FaUndo className="mx-auto" size={14} />
                        </button>
                        <button
                          onClick={clearCanvas}
                          className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-all text-xs"
                          title="Limpiar"
                        >
                          <FaTrash className="mx-auto" size={14} />
                        </button>
                        <button
                          onClick={() => setShowSaveDialog(true)}
                          className="p-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-all text-xs"
                          title="Guardar"
                        >
                          <FaSave className="mx-auto" size={14} />
                        </button>
                        <button
                          onClick={() => setShowGallery(true)}
                          className="p-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition-all text-xs"
                          title="Galería"
                        >
                          <FaImages className="mx-auto" size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* Shared Canvas Container - Always rendered */}
        <div className={isLandscape ? "flex-1 flex flex-col items-center justify-center overflow-hidden" : "bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl shadow-2xl p-4 sm:p-6 mx-4 space-y-4"}>

          {/* Portrait Mode - Full Toolbar Visible */}
          {!isLandscape && (
            <>
            {/* Toolbar */}
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-between bg-white/5 p-4 rounded-lg">

              {/* Tools */}
              <div className="flex gap-2 flex-wrap justify-center">
                <button
                  onClick={() => setCurrentTool('draw')}
                  className={`p-3 rounded-lg transition-all ${
                    currentTool === 'draw'
                      ? 'bg-blue-500 text-white scale-110'
                      : 'bg-white/10 hover:bg-white/20 text-white'
                  }`}
                  title="Pincel"
                >
                  <FaPaintBrush />
                </button>
                <button
                  onClick={() => setCurrentTool('erase')}
                  className={`p-3 rounded-lg transition-all ${
                    currentTool === 'erase'
                      ? 'bg-blue-500 text-white scale-110'
                      : 'bg-white/10 hover:bg-white/20 text-white'
                  }`}
                  title="Borrador"
                >
                  <FaEraser />
                </button>
                <button
                  onClick={() => setCurrentTool('fill')}
                  className={`p-3 rounded-lg transition-all ${
                    currentTool === 'fill'
                      ? 'bg-blue-500 text-white scale-110'
                      : 'bg-white/10 hover:bg-white/20 text-white'
                  }`}
                  title="Relleno"
                >
                  <FaFill />
                </button>
              </div>

              {/* Color Palette */}
              <div className="flex gap-2 flex-wrap justify-center">
                {colors.map((color) => (
                  <button
                    key={color}
                    onClick={() => setCurrentColor(color)}
                    className={`w-8 h-8 rounded-full transition-all ${
                      currentColor === color
                        ? 'ring-4 ring-white scale-110'
                        : 'hover:scale-105'
                    }`}
                    style={{
                      backgroundColor: color,
                      border: color === '#FFFFFF' ? '2px solid #666' : 'none',
                    }}
                    title={color}
                  />
                ))}
                <input
                  type="color"
                  value={currentColor}
                  onChange={(e) => setCurrentColor(e.target.value)}
                  className="w-8 h-8 rounded-full cursor-pointer"
                  title="Color personalizado"
                />
              </div>

              {/* Brush Size */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-300">Tamaño:</span>
                <select
                  value={lineWidth}
                  onChange={(e) => setLineWidth(Number(e.target.value))}
                  className="bg-white/10 px-3 py-2 rounded-lg text-white border border-white/20 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {brushSizes.map((size) => (
                    <option key={size} value={size} className="bg-gray-800">
                      {size}px
                    </option>
                  ))}
                </select>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <button
                  onClick={undoLastAction}
                  className="p-3 bg-yellow-500/20 hover:bg-yellow-500/30 rounded-lg transition-all flex items-center gap-2 text-white"
                  title="Deshacer"
                >
                  <FaUndo />
                  <span className="hidden sm:inline text-sm">Deshacer</span>
                </button>
                <button
                  onClick={clearCanvas}
                  className="p-3 bg-red-500/20 hover:bg-red-500/30 rounded-lg transition-all flex items-center gap-2 text-white"
                  title="Limpiar"
                >
                  <FaTrash />
                  <span className="hidden sm:inline text-sm">Limpiar</span>
                </button>
                <button
                  onClick={() => setShowSaveDialog(true)}
                  className="p-3 bg-green-500/20 hover:bg-green-500/30 rounded-lg transition-all flex items-center gap-2 text-white"
                  title="Guardar"
                >
                  <FaSave />
                  <span className="hidden sm:inline text-sm">Guardar</span>
                </button>
                <button
                  onClick={() => setShowGallery(true)}
                  className="p-3 bg-purple-500/20 hover:bg-purple-500/30 rounded-lg transition-all flex items-center gap-2 text-white"
                  title="Galería"
                >
                  <FaImages />
                  <span className="hidden sm:inline text-sm">Galería</span>
                </button>
              </div>
            </div>

            {/* Instructions */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4"
            >
              <h3 className="font-bold mb-2 text-purple-300">✨ Consejos:</h3>
              <ul className="text-sm text-gray-300 space-y-1">
                <li>📱 <strong>Modo horizontal:</strong> Gira tu teléfono para pantalla completa</li>
                <li>🎨 <strong>Colaborativo:</strong> Ambos pueden dibujar al mismo tiempo en tiempo real</li>
                <li>💾 <strong>Guardar:</strong> Guarda tus creaciones para verlas en la galería</li>
              </ul>
            </motion.div>
            </>
          )}

          {/* Single Canvas - Always rendered, styled differently based on orientation */}
          <div className={isLandscape ? "relative bg-white shadow-2xl flex items-center justify-center w-full h-full" : "relative bg-white rounded-lg shadow-2xl overflow-hidden max-w-full"}>
            <canvas
              ref={canvasRef}
              width={canvasWidth}
              height={canvasHeight}
              onMouseDown={handleStart}
              onMouseMove={handleMove}
              onMouseUp={handleEnd}
              onMouseLeave={handleEnd}
              onTouchStart={handleStart}
              onTouchMove={handleMove}
              onTouchEnd={handleEnd}
              className={isLandscape ? "cursor-crosshair" : "cursor-crosshair w-full h-auto"}
              style={
                isLandscape
                  ? {
                      touchAction: 'none',
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                    }
                  : {
                      touchAction: 'none',
                      maxWidth: '100%',
                      height: 'auto',
                    }
              }
            />
            {isDrawing && (
              <div className="absolute top-2 left-2 bg-blue-500 text-white px-3 py-1 rounded-full text-xs">
                Dibujando...
              </div>
            )}
          </div>

          {/* Active Users - Below Canvas */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className={isLandscape ? "flex items-center justify-center gap-2 text-sm text-white mt-2 bg-black/50 px-4 py-2 rounded-full" : "flex items-center justify-center gap-2 text-sm text-gray-300"}
          >
            <FaUser className="text-green-400" />
            <span>Activos: {activeUsers.size > 0 ? Array.from(activeUsers).join(', ') : 'Solo tú'}</span>
          </motion.div>
        </div>
      </div>

      {/* Save Dialog Modal */}
      <AnimatePresence>
        {showSaveDialog && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white/95 backdrop-blur-md rounded-2xl p-6 max-w-md w-full shadow-2xl border border-white/20"
            >
              <h3 className="text-xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-4">
                💾 Guardar Dibujo
              </h3>

              <input
                type="text"
                placeholder="Dale un nombre a tu obra de arte..."
                value={drawingName}
                onChange={(e) => setDrawingName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSaveDrawing()}
                className="w-full p-4 border border-gray-200 rounded-xl mb-4 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent bg-white/80 backdrop-blur-sm"
                autoFocus
              />

              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowSaveDialog(false);
                    setDrawingName('');
                  }}
                  className="px-6 py-3 bg-gray-200/80 text-gray-700 rounded-xl hover:bg-gray-300/80 transition-all duration-200 flex items-center gap-2 font-medium"
                >
                  <FaTimes className="w-4 h-4" />
                  Cancelar
                </button>

                <button
                  onClick={handleSaveDrawing}
                  disabled={!drawingName.trim() || isSaving}
                  className="px-6 py-3 bg-gradient-to-r from-green-500 to-teal-500 text-white rounded-xl hover:from-green-600 hover:to-teal-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium shadow-lg"
                >
                  <FaCheck className="w-4 h-4" />
                  {isSaving ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </section>
  );
};
